/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE. 
*/

import { CosmosText, EdsIcon } from "@components/elements";
import { LicenseSeatFragment } from "@cosmos/hygraph-schema";
import { ChangeEventHandler, FocusEventHandler, MouseEventHandler } from "react";

import { css, cx } from "@panda/css";
import { HStack, VStack } from "@panda/jsx";

import { LicenseCardDescription } from "./LicenseCardDescription.molecule";
import { PriceRes } from "@cosmos/next-hooks";

interface Props extends LicenseSeatFragment {
	locale: string;
	priceNum: number;
	priceStr: string;
	className?: string;
	currencyCode?: string | null;
	seatCount: number;
	country: string;
	price?: PriceRes;
	onDecrementClick: MouseEventHandler<HTMLButtonElement>;
	onIncrementClick: MouseEventHandler<HTMLButtonElement>;
	onInputChange: ChangeEventHandler<HTMLInputElement>;
	onInputBlur: FocusEventHandler<HTMLInputElement>;
}

const buttonStyles = css({
	padding: "eds.dimension.8",
	borderWidth: "1px",
	borderColor: "eds.border.default",
	borderRadius: "eds.dimension.8",
	borderStyle: "solid",
	cursor: "pointer",
	color: "eds.text.primary",
	width: "40px",
	height: "40px",
	display: "flex",
	justifyContent: "center",
	alignItems: "center",
	background: "eds.background.elevated.high.default",
	"&:hover": {
		borderColor: "eds.border.hover",
	},
	"&[disabled]": {
		cursor: "default",
		borderColor: "eds.border.disabled",
		"&:hover": {
			borderColor: "eds.border.disabled",
		},
	},
});

const inputStyles = css({
	"&&": {
		cursor: "text",
		background: "eds.fill.input.default",
		color: "eds.text.secondary",
		width: "44px",
		appearance: "textfield",
		textStyle: "uiMD",
		textAlign: "center",
	},
	"&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
		WebkitAppearance: "none",
		margin: "0",
	},
});

export const LicenseCardSeats = ({
	className,
	title,
	description,
	perSeatPerYear,
	addButtonLabel,
	minusButtonLabel,
	perYear,
	taxes,
	locale,
	seatCount,
	priceNum,
	priceStr,
	currencyCode,
	onDecrementClick,
	onIncrementClick,
	onInputChange,
	onInputBlur,
}: Props): React.JSX.Element => {
	const pricePerYearStr = new Intl.NumberFormat(locale, {
		style: "currency",
		currency: currencyCode || "USD",
	})
		.format(priceNum * seatCount)
		.replace(/\.00$/, "");

	const isMinusDisabled = seatCount <= 1 || isNaN(seatCount);
	const isPlusDisabled = seatCount >= 25 || isNaN(seatCount);

	return (
		<>
			<VStack
				alignItems="flex-start"
				gap="eds.dimension.16"
				paddingBlockEnd="eds.dimension.56"
				w="full"
				marginTop="auto"
				position="relative"
				className={cx(css({ smDown: { paddingBlockEnd: "eds.dimension.32" } }), className)}
			>
				<CosmosText variant="headingLG">{title}</CosmosText>
				<HStack
					justifyContent="space-between"
					w="full"
					alignItems="flex-start"
					className={css({ smDown: { flexDirection: "column" } })}
				>
					<HStack gap="eds.dimension.16" className={css({ smDown: { flexDirection: "column" } })}>
						<HStack gap="eds.dimension.8">
							<button
								aria-label={minusButtonLabel || "Remove seat"}
								className={buttonStyles}
								disabled={isMinusDisabled}
								onClick={onDecrementClick}
							>
								<EdsIcon icon="Minus" size="sm" color={isMinusDisabled ? "secondary" : "contrast"} />
							</button>
							<input
								className={cx(buttonStyles, inputStyles)}
								type="number"
								aria-label={addButtonLabel || "Add seat"}
								value={seatCount.toString()}
								onChange={onInputChange}
								onBlur={onInputBlur}
							/>
							<button
								aria-label={addButtonLabel || "Add seat"}
								className={buttonStyles}
								disabled={isPlusDisabled}
								onClick={onIncrementClick}
							>
								<EdsIcon icon="Plus" size="sm" color={isPlusDisabled ? "secondary" : "contrast"} />
							</button>
						</HStack>
						<VStack alignItems="flex-start" gap="0">
							<HStack>
								<CosmosText variant="paragraphSM" color="eds.text.primary" className={css({ fontWeight: 700 })}>
									{priceStr}&nbsp;{perSeatPerYear}
								</CosmosText>
							</HStack>
							<CosmosText variant="paragraphSM" color="eds.text.secondary">
								{taxes}
							</CosmosText>
						</VStack>
					</HStack>
					<CosmosText variant="paragraphSM" color="eds.text.primary" className={css({ fontWeight: 700 })}>
						{isNaN(seatCount) ? null : (
							<>
								{pricePerYearStr}
								&nbsp;{perYear}
							</>
						)}
					</CosmosText>
				</HStack>
				<LicenseCardDescription html={description?.html} textStyle="paragraphSM" />
			</VStack>
		</>
	);
};
